import { useCallback, useLayoutEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import UIIcon from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'
import { useTransComponents } from '@/composables/useTransComponents'
import { UIAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'

export type ConversationBackgroundItemProps = {
  index: number
  hoverIndex: number | null
  isReady: boolean
  subject: string
  position: string
  choice: {
    value: string
    i18nKey: string
    ariaLabelI18nKey: string
  }
  setHoverIndex: (index: number | null) => void
  onChoose?: (choice: string) => void
}

export default function ConversationBackgroundItem({
  index,
  hoverIndex,
  isReady,
  subject,
  position,
  choice,
  setHoverIndex,
  onChoose,
}: ConversationBackgroundItemProps) {
  const { components } = useTransComponents()
  const { t } = useTranslation()

  const { gsap } = useGSAP()

  const arrowRef = useRef<SVGSVGElement>(null)
  const arrowBodyRef = useRef<SVGPathElement>(null)
  const arrowHeadRef = useRef<SVGPathElement>(null)
  const arrowBodyAltRef = useRef<SVGPathElement>(null)
  const arrowHeadAltRef = useRef<SVGPathElement>(null)
  const bgRef = useRef<SVGSVGElement>(null)

  const isHover = hoverIndex !== null && index === hoverIndex
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isHover) {
        gsap
          .timeline({ delay: 0 })
          .addLabel('arrow', 0)
          .fromTo(
            [arrowBodyAltRef.current, arrowBodyRef.current],
            { drawSVG: '0% 0%' },
            {
              drawSVG: '0% 100%',
              duration: 0.5,
              ease: 'power2.out',
              stagger: 0.15,
            },
            'arrow'
          )
          .fromTo(
            [arrowHeadAltRef.current, arrowHeadRef.current],
            { drawSVG: '50% 50%' },
            {
              drawSVG: '0% 100%',
              duration: 0.1,
              ease: 'power2.out',
              stagger: 0.15,
            },
            'arrow+=0.45'
          )
      } else {
        gsap.timeline().to(arrowRef.current, { opacity: 0, duration: 0.3, ease: 'power2.out' }, 0)
      }

      gsap
        .timeline()
        .fromTo(bgRef.current, { scale: 0 }, { scale: 1, duration: 0.333, ease: 'back.out(1.7)' }, 0.25 * index)
        .fromTo(
          bgRef.current,
          {
            // y: -60,
            rotate: 22.5,
          },
          {
            x: 0,
            y: 0,
            rotate: 0,
            duration: 0.3,
            ease: 'power3.inOut',
          },
          0.35 * index
        )
    })

    return () => ctx.revert()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHover, arrowBodyRef, arrowHeadRef, gsap])

  const onMouseMove = useCallback(() => {
    if (index !== hoverIndex) {
      AudioEngine.play(UIAudio.Hover)
    }
    setHoverIndex(index)
  }, [index, hoverIndex, setHoverIndex])

  // useLayoutEffect(() => {
  //   if (isHover) {
  //     // AudioEngine.play(UIAudio.Hover);
  //     console.log('isHover', { isHover })
  //   }
  // }, [isHover])

  return (
    <div
      className={`z-1 relative md:absolute md:-translate-x-1/2 md:-translate-y-1/2 block w-128 h-128 md:w-258 md:h-258 transition-opacity duration-333 ease-out-quad ${position} ${
        (hoverIndex !== null && index !== hoverIndex) || !isReady ? 'opacity-0' : ''
      }`}
    >
      {/* <input className="absolute top-0 left-0 opacity-0" type="radio" name={subject} value={choice.value} id={`${subject}-${index}`} /> */}
      <UIIcon
        ref={bgRef}
        icon={gsap.utils.wrap(['background1', 'background2', 'background3'], index)}
        className={`md:hidden absolute inset-0 text-[128px] ${gsap.utils.wrap(['text-mid-blue', 'text-coral'], index)}`}
      />

      <button
        className={`relative outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-scarlet w-full h-full md:p-64 flex items-center justify-center rounded-full text-center antialiased font-normal leading-relaxed text-28 lg:text-32 ${gsap.utils.wrap(
          ['text-cream', 'text-black-blue'],
          index
        )} ${isReady ? 'pointer-events-auto' : ''}`}
        onClick={() => {
          AudioEngine.play(UIAudio.ClickAlt)
          setHoverIndex(null)
          onChoose?.(choice.value)
        }}
        type='button'
        aria-label={t(choice.ariaLabelI18nKey)}
      >
        <Trans i18nKey={choice.i18nKey} components={components} />
      </button>

      <svg
        aria-hidden
        ref={arrowRef}
        className={`hidden md:block pointer-events-none absolute -top-72 -left-48 w-80 h-80 rotate-90 transition-opacity duration-333 ease-out-quad ${
          hoverIndex !== null && index === hoverIndex ? 'opacity-100' : 'opacity-0'
        }`}
        fill='none'
      >
        <path
          ref={arrowBodyAltRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowHeadAltRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowBodyRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
        />
        <path
          ref={arrowHeadRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
        />
      </svg>
    </div>
  )
}
